import {
  Box,
  ClickAwayListener,
  Fade,
  FormControl,
  FormControlLabel,
  makeStyles,
  Paper,
  Popper,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import React, { FC, MouseEventHandler, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useWeglot } from '../../hooks/useWeglot';
import { useShopConfig } from '../../hooks/shopConfig';
import { InlineTextButton } from '../Button/Button';
import ExternalLink from '../ExternalLink/ExternalLink';
import TypographyWrapper, { TypographyFormat } from '../TypographyWrapper/TypographyWrapper';
import css from './LanguageSwitcher.module.css';
import { REPORT_TRANSLATION_PROBLEM_SURVEY_LINK } from '../../util/constants';

const useStyles = makeStyles(() => ({
  formGroup: {
    gap: '2px',
  },
  formControlLabel: {
    padding: 0,
    margin: 0,
  },
}));

export const LanguageSwitcher: FC = () => {
  const { formGroup, formControlLabel } = useStyles();

  const { weglot } = useWeglot();
  const { css: brandCss } = useShopConfig();

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!anchorEl) {
      setAnchorEl(e.currentTarget);
      return;
    }
    handleClose();
  };

  const { control, watch, reset } = useForm<{
    language: string | undefined;
  }>({
    defaultValues: {
      language: undefined,
    },
  });

  // When weglot initializes, set the form to the current language.
  useEffect(() => {
    if (weglot) {
      const currentLanguage = weglot.getCurrentLang();
      reset({
        language: currentLanguage,
      });
    }
  }, [weglot?.initialized, reset]);

  // Sync weglot to the selected language. Close switcher on select.
  const selectedLanguage = watch('language');
  useEffect(() => {
    if (weglot && selectedLanguage) {
      weglot.switchTo(selectedLanguage);
    }
    handleClose();
  }, [weglot?.initialized, selectedLanguage]);

  if (!weglot) return null;

  const getLanguageObjectFromWeglot = (code: string) => ({
    name: weglot.getLanguageName(code),
    code,
  });

  const availableLanguages = weglot
    .getAvailableLanguages()
    .map((code) => getLanguageObjectFromWeglot(code));

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box display="flex">
        <InlineTextButton
          onClick={handleClick}
          className={css.button}
          typographyVariant="subtitle1"
          typographyFormat={TypographyFormat.Underlined}
          typographyIsDynamicValue
        >
          {
            selectedLanguage
              ? getLanguageObjectFromWeglot(selectedLanguage).name
              : 'Change language' // Fallback if Weglot fails to preselect a language
          }
        </InlineTextButton>
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{
            zIndex: 'var(--zIndexPopup)',
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps}>
              <Paper
                className={css.menuContainer}
                style={{ backgroundColor: brandCss?.backgroundColor || 'white' }}
              >
                <FormControl component="fieldset">
                  <Controller
                    control={control}
                    name="language"
                    render={({ field }) => (
                      <RadioGroup {...field} aria-label="Language switcher" className={formGroup}>
                        {availableLanguages.map((lang) => (
                          <FormControlLabel
                            key={lang.code}
                            className={formControlLabel}
                            value={lang.code}
                            control={<Radio />}
                            label={
                              <TypographyWrapper
                                variant="body1"
                                isDynamicValue
                                typographyOverrides={{
                                  style: {
                                    fontStyle: 'italic',
                                  },
                                }}
                              >
                                {lang.name}
                              </TypographyWrapper>
                            }
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </FormControl>
                <ExternalLink href={REPORT_TRANSLATION_PROBLEM_SURVEY_LINK}>
                  <TypographyWrapper
                    variant="body2"
                    format={TypographyFormat.Underlined}
                    typographyOverrides={{
                      style: {
                        fontStyle: 'italic',
                      },
                    }}
                  >
                    Translations Feedback
                  </TypographyWrapper>
                </ExternalLink>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
